import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
} from "@mui/material";
import ArchitectureIcon from "@mui/icons-material/Architecture";

import architectureImg from "../../pictures/img1.jpg";

const services = [
  {
    title: "House Planning",
    desc: "Smart layouts with maximum comfort. Thoughtfully designed spaces that maximize functionality.",
    accent: "#4f6dff",
  },
  {
    title: "3D Elevation Design",
    desc: "Visualize before you build. Stunning concepts that bring your dream structure to life.",
    accent: "#7c5cff",
  },
  {
    title: "Structural Drawings",
    desc: "Engineered for strength. Precision plans ensuring safety and long-term durability.",
    accent: "#00a0d2",
  },
  {
    title: "BOQ & Cost Planning",
    desc: "Clear cost, no surprises. Transparent estimation with schedule of work.",
    accent: "#6d7cff",
  },
];

function ArchitectureService() {
  return (
    <Box>
      {/* HERO */}
      <Box
        sx={{
          position: "relative",
          minHeight: { xs: 320, md: 420 },
          background: `linear-gradient(rgba(13,6,81,0.68), rgba(13,6,81,0.88)), url(${architectureImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          textAlign: "center",
          overflow: "hidden",
        }}
      >
        {/* floating background glow */}
        <Box
          sx={{
            position: "absolute",
            width: 240,
            height: 240,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255,255,255,0.20), rgba(255,255,255,0.02))",
            top: "8%",
            left: "8%",
            filter: "blur(8px)",
            animation: "floatGlow 8s ease-in-out infinite",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(79,109,255,0.35), rgba(79,109,255,0.04))",
            bottom: "10%",
            right: "10%",
            filter: "blur(10px)",
            animation: "floatGlow 10s ease-in-out infinite 1s",
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            px: 4,
            py: 4,
            borderRadius: 4,
            background: "rgba(255,255,255,0.08)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.14)",
            boxShadow: "0 20px 50px rgba(0,0,0,0.24)",
            animation: "heroPanelFloat 6s ease-in-out infinite",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "2rem", md: "3.2rem" },
              fontWeight: 800,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Architecture Design
          </Typography>

          <Typography
            sx={{
              mt: 1.5,
              color: "rgba(255,255,255,0.88)",
              maxWidth: 700,
              mx: "auto",
              lineHeight: 1.8,
              fontSize: { xs: "0.95rem", md: "1.05rem" },
            }}
          >
            Smart architectural solutions with elegant planning, clear technical thinking, and strong visual presentation.
          </Typography>
        </Box>
      </Box>

      {/* SECTION */}
      <Box
        sx={{
          py: 10,
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(135deg, #f5f7ff 0%, #eef1ff 45%, #f8fbff 100%)",
        }}
      >
        {/* soft background blobs */}
        <Box
          sx={{
            position: "absolute",
            top: -100,
            left: -60,
            width: 240,
            height: 240,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(79,109,255,0.10) 0%, rgba(79,109,255,0) 70%)",
            filter: "blur(16px)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -100,
            right: -50,
            width: 260,
            height: 260,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(13,6,81,0.08) 0%, rgba(13,6,81,0) 70%)",
            filter: "blur(18px)",
          }}
        />

        <Container sx={{ position: "relative", zIndex: 2 }}>
          <Typography
            variant="h4"
            textAlign="center"
            fontWeight="bold"
            mb={2}
            sx={{ color: "#100e62" }}
          >
            Smart Architectural Solutions
          </Typography>

          <Typography
            textAlign="center"
            sx={{
              maxWidth: 760,
              mx: "auto",
              mb: 8,
              color: "rgba(13,6,81,0.72)",
              lineHeight: 1.8,
            }}
          >
            From concept planning to structural coordination, our design services are shaped to make every project more practical, elegant, and execution-ready.
          </Typography>

          <Grid container spacing={5}>
            {services.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index} display="flex">
                <Box
                  sx={{
                    perspective: "1400px",
                    width: "100%",
                  }}
                >
                  <Card
                    sx={{
                      height: 320,
                      width: "100%",
                      borderRadius: 5,
                      isolation: "isolate",
                      position: "relative",
                      overflow: "hidden",
                      cursor: "pointer",
                      background: "rgba(255,255,255,0.62)",
                      backdropFilter: "blur(14px)",
                      border: "1px solid rgba(255,255,255,0.35)",
                      boxShadow: "0 20px 45px rgba(16,14,98,0.10)",
                      transformStyle: "preserve-3d",
                      transform:
                        index % 2 === 0
                          ? "rotateY(-7deg) rotateX(5deg)"
                          : "rotateY(7deg) rotateX(5deg)",
                      transition: "all 0.45s ease",
                      animation: `serviceFloat 6s ease-in-out ${index * 0.2}s infinite`,
                      "&:hover": {
                        transform: "rotateY(0deg) rotateX(0deg) translateY(-14px) scale(1.03)",
                        boxShadow: "0 28px 60px rgba(16,14,98,0.18)",
                      },
                      "&:hover .overlay": {
                        transform: "translateY(0%)",
                      },
                      "&:hover .iconWrap": {
                        transform: "translateZ(40px) rotate(12deg) scale(1.08)",
                        background: `linear-gradient(145deg, ${item.accent}, #100e62)`,
                      },
                      "&:hover .icon": {
                        transform: "rotate(360deg) scale(1.16)",
                        color: "#fff",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        borderRadius: "20px",
                        background: `linear-gradient(145deg, ${item.accent}20, rgba(255,255,255,0.06))`,
                        transform: "translateZ(-18px) translateY(12px)",
                        zIndex: 0,
                      },
                    }}
                  >
                    {/* DEFAULT CONTENT */}
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 3,
                        position: "relative",
                        zIndex: 1,
                        textAlign: "center",
                      }}
                    >
                      <Box
                        className="iconWrap"
                        sx={{
                          width: 84,
                          height: 84,
                          borderRadius: "24px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "linear-gradient(145deg, #ffffff, #e8ecff)",
                          boxShadow: "0 18px 35px rgba(0,0,0,0.10)",
                          transition: "all 0.6s ease",
                          mb: 2.5,
                        }}
                      >
                        <ArchitectureIcon
                          className="icon"
                          sx={{
                            fontSize: 44,
                            color: "#100e62",
                            transition: "0.7s",
                          }}
                        />
                      </Box>

                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: "1.08rem",
                          color: "#151444",
                          lineHeight: 1.5,
                        }}
                      >
                        {item.title}
                      </Typography>

                      <Box
                        sx={{
                          mt: 2,
                          width: 56,
                          height: 4,
                          borderRadius: 999,
                          background: `linear-gradient(90deg, ${item.accent}, #100e62)`,
                        }}
                      />
                    </Box>

                    {/* HOVER OVERLAY */}
                    <Box
                      className="overlay"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background:
                          "linear-gradient(180deg, rgba(13,6,81,0.82), rgba(13,6,81,0.96))",
                        color: "#fff",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        p: 3,
                        transform: "translateY(100%)",
                        transition: "transform 0.55s ease",
                        backdropFilter: "blur(8px)",
                        zIndex: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "1.05rem",
                          mb: 1.5,
                          color: "#fff",
                        }}
                      >
                        {item.title}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 14.5,
                          lineHeight: 1.8,
                          letterSpacing: 0.3,
                          color: "rgba(255,255,255,0.90)",
                        }}
                      >
                        {item.desc}
                      </Typography>
                    </Box>
                  </Card>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <style>{`
        @keyframes serviceFloat {
          0%, 100% {
            transform: translateY(0px) rotateX(5deg);
          }
          50% {
            transform: translateY(-12px) rotateX(2deg);
          }
        }

        @keyframes heroPanelFloat {
          0%, 100% {
            transform: translateY(0px) rotateX(0deg);
          }
          50% {
            transform: translateY(-10px) rotateX(2deg);
          }
        }

        @keyframes floatGlow {
          0%, 100% {
            transform: translateY(0px) translateX(0px) scale(1);
          }
          50% {
            transform: translateY(-18px) translateX(10px) scale(1.05);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </Box>
  );
}

export default ArchitectureService;